/**
 * AdminContactInbox — super-admin triage view for all memorial
 * "Contact us" requests. Lives at /admin/contact-requests.
 *
 * Layout:
 *   Left rail  — status chips (New / Reviewing / Resolved / Rejected / All)
 *                + reason filter select + search box
 *   Right side — list of requests. Click a row to expand full message,
 *                see any attachment thumbnail, and act on it
 *                (Mark reviewing / Mark resolved / Reject + admin note).
 *
 * Uses GET  /api/admin/contact-requests  and
 *      PATCH /api/admin/contact-requests/{id}
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Inbox, Loader2, Search, ExternalLink, Paperclip, ChevronRight,
  CheckCircle2, Clock, XCircle, RefreshCw, MessageCircle, Send,
} from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Helmet } from "react-helmet-async";

const STATUS_META = {
  new:       { label: "New",       Icon: Inbox,        color: "#DC2626", bg: "#FEF2F2" },
  reviewing: { label: "Reviewing", Icon: Clock,        color: "#D97706", bg: "#FFFBEB" },
  resolved:  { label: "Resolved",  Icon: CheckCircle2, color: "#059669", bg: "#ECFDF5" },
  rejected:  { label: "Rejected",  Icon: XCircle,      color: "#6B7280", bg: "#F3F4F6" },
};

const REASON_OPTIONS = [
  { v: "",                 label: "All reasons" },
  { v: "correction",       label: "Correction" },
  { v: "additional_info",  label: "Additional info" },
  { v: "privacy_concern",  label: "Privacy concern" },
  { v: "takedown_request", label: "Takedown request" },
  { v: "duplicate",        label: "Duplicate" },
  { v: "general_question", label: "General question" },
  { v: "other",            label: "Other" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const secs = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60)         return `${secs}s ago`;
  if (secs < 3600)       return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400)      return `${Math.round(secs / 3600)}h ago`;
  if (secs < 30 * 86400) return `${Math.round(secs / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function AdminContactInbox() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("new");
  const [reasonFilter, setReasonFilter] = useState("");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (reasonFilter) params.reason = reasonFilter;
      if (q.trim())     params.q = q.trim();
      const { data } = await http.get("/admin/contact-requests", { params });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load inbox.");
      setItems([]);
    } finally { setLoading(false); }
  }, [statusFilter, reasonFilter, q]);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(
    () => (items || []).find((it) => it.id === selectedId) || null,
    [items, selectedId]
  );

  const onAction = async (id, patch) => {
    try {
      await http.patch(`/admin/contact-requests/${id}`, patch);
      toast.success("Updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update.");
    }
  };

  if (authLoading) return <div className="p-10 text-center opacity-60"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!user?.is_admin) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center">
        <h1 className="font-display text-2xl">Admins only</h1>
        <p className="mt-2 opacity-70">You need super-admin access to view this inbox.</p>
        <Link to="/" className="wofy-btn-primary mt-4 inline-flex">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-10 px-4">
      <Helmet><title>Contact Inbox · Admin · Social Pulse</title></Helmet>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
        <div>
          <div className="overline" style={{ color: "var(--wofy-primary)" }}>ADMIN</div>
          <h1 className="font-display text-3xl">Memorial contact inbox</h1>
          <p className="text-sm opacity-70 mt-1">Triage messages sent via the "Contact us" button on every memorial.</p>
        </div>
        <button onClick={load} className="wofy-btn-ghost text-sm" data-testid="admin-inbox-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        {/* Filters */}
        <aside className="md:col-span-1 space-y-3">
          <div className="wofy-card p-3">
            <div className="text-xs font-medium mb-2">Status</div>
            {[
              { k: "",          label: "All"        },
              { k: "new",       label: "New"        },
              { k: "reviewing", label: "Reviewing"  },
              { k: "resolved",  label: "Resolved"   },
              { k: "rejected",  label: "Rejected"   },
            ].map((s) => (
              <button
                key={s.k || "all"}
                onClick={() => { setStatusFilter(s.k); setSelectedId(null); }}
                className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-slate-50 flex justify-between items-center"
                style={statusFilter === s.k ? { fontWeight: 600, color: "#4338CA", background: "#EEF2FF" } : {}}
                data-testid={`admin-inbox-status-${s.k || "all"}`}
              >
                <span>{s.label}</span>
                <span className="opacity-60 text-xs">{counts[s.k || "all"] ?? (s.k ? counts[s.k] : counts.all) ?? 0}</span>
              </button>
            ))}
          </div>
          <div className="wofy-card p-3">
            <div className="text-xs font-medium mb-2">Reason</div>
            <select className="wofy-input" value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)} data-testid="admin-inbox-reason">
              {REASON_OPTIONS.map((r) => <option key={r.v || "all"} value={r.v}>{r.label}</option>)}
            </select>
          </div>
          <div className="wofy-card p-3">
            <div className="text-xs font-medium mb-2 flex items-center gap-1"><Search className="w-3 h-3" /> Search</div>
            <input
              className="wofy-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Name / message / memorial"
              data-testid="admin-inbox-search"
            />
          </div>
        </aside>

        {/* List */}
        <section className="md:col-span-3 space-y-2">
          {loading && items == null && <div className="py-10 text-center opacity-60"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
          {items && items.length === 0 && (
            <div className="wofy-card text-sm text-center opacity-70">No contact requests match these filters.</div>
          )}
          {(items || []).map((it) => {
            const meta = STATUS_META[it.status] || STATUS_META.new;
            const StatusIcon = meta.Icon;
            const open = selectedId === it.id;
            return (
              <div key={it.id} className="wofy-card p-4" data-testid={`admin-inbox-row-${it.id}`}>
                <button
                  onClick={() => setSelectedId(open ? null : it.id)}
                  className="w-full text-left flex items-start gap-3"
                >
                  <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: meta.bg, color: meta.color }}>
                    <StatusIcon className="w-3 h-3" /> {meta.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{it.contact_name} · <span className="opacity-70">{it.reason.replace(/_/g, " ")}</span></div>
                    <div className="text-xs opacity-70 mt-0.5 truncate">Re: {it.memorial_full_name || it.memorial_slug} · {timeAgo(it.created_at)}</div>
                    {!open && <div className="text-sm mt-1 opacity-80 line-clamp-1">{it.message}</div>}
                  </div>
                  {it.attachment_url && <Paperclip className="w-4 h-4 opacity-50 flex-shrink-0" />}
                  <ChevronRight className={`w-4 h-4 opacity-40 flex-shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
                </button>
                {open && (
                  <div className="mt-3 border-t pt-3" style={{ borderColor: "var(--wofy-border)" }} data-testid={`admin-inbox-detail-${it.id}`}>
                    <div className="grid md:grid-cols-2 gap-3 text-sm">
                      <div><span className="opacity-60">Email:</span> {it.contact_email || "—"}</div>
                      <div><span className="opacity-60">Phone:</span> {it.contact_phone || "—"}</div>
                    </div>
                    <blockquote className="text-sm mt-3 whitespace-pre-wrap pl-3 border-l-2" style={{ borderColor: meta.color }}>
                      {it.message}
                    </blockquote>
                    {it.attachment_url && (
                      <a
                        href={it.attachment_url}
                        target="_blank" rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border hover:bg-slate-50"
                        style={{ borderColor: "var(--wofy-border)" }}
                        data-testid={`admin-inbox-attach-${it.id}`}
                      >
                        <Paperclip className="w-3 h-3" /> {it.attachment_name || "Attachment"} <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Link
                        to={`/celebrate/${it.memorial_slug || it.memorial_id}`}
                        target="_blank"
                        className="wofy-btn-ghost text-xs" rel="noreferrer"
                        data-testid={`admin-inbox-open-mem-${it.id}`}
                      >
                        <ExternalLink className="w-3 h-3" /> Open memorial
                      </Link>
                      <div className="flex items-center gap-1 ml-auto">
                        {["reviewing", "resolved", "rejected"].filter((s) => s !== it.status).map((s) => {
                          const sm = STATUS_META[s];
                          return (
                            <button
                              key={s}
                              onClick={() => onAction(it.id, { status: s })}
                              className="text-xs px-3 py-1.5 rounded-full border hover:opacity-100 opacity-90"
                              style={{ borderColor: sm.color, color: sm.color, background: sm.bg }}
                              data-testid={`admin-inbox-set-${s}-${it.id}`}
                            >
                              Mark {sm.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <AdminNoteForm req={it} onSubmit={(txt) => onAction(it.id, { admin_notes: txt })} />
                    {(it.admin_note_trail || []).length > 0 && (
                      <div className="mt-3">
                        <div className="text-[10px] font-mono uppercase opacity-60 mb-1">Admin note history</div>
                        <ul className="space-y-1">
                          {(it.admin_note_trail || []).map((n, i) => (
                            <li key={i} className="text-xs opacity-80 border-l-2 pl-2" style={{ borderColor: "var(--wofy-border)" }}>
                              <span className="opacity-60">{timeAgo(n.created_at)} · {n.actor_name || "admin"}:</span> {n.text}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </section>
      </div>
    </div>
  );
}

function AdminNoteForm({ req, onSubmit }) {
  const [text, setText] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (text.trim().length < 2) return;
    onSubmit(text.trim());
    setText("");
  };
  return (
    <form onSubmit={submit} className="mt-3 flex gap-2" data-testid={`admin-inbox-note-form-${req.id}`}>
      <input
        className="wofy-input flex-1 text-sm"
        placeholder="Add an admin note (kept on record + on the memorial audit trail)"
        value={text} onChange={(e) => setText(e.target.value)}
        data-testid={`admin-inbox-note-input-${req.id}`}
      />
      <button type="submit" className="wofy-btn-primary text-xs" data-testid={`admin-inbox-note-submit-${req.id}`}>
        <Send className="w-3 h-3" /> Log
      </button>
    </form>
  );
}
